import { TilePosition, DashboardLayout } from '../types/dashboard';

const DEFAULT_GRID_COLS = 6; // Standard 6-column grid system
const GRID_CELL_HEIGHT = 100; // Base height per grid cell in pixels
const GRID_GAP = 12; // Gap between tiles in pixels

export class LayoutService {
  /**
   * Generate default layout for given tile IDs
   * Uses proven algorithm from original codebase
   */
  static getDefaultLayout(tileIds: string[]): DashboardLayout {
    const tiles: TilePosition[] = [];
    let x = 0;
    let y = 0;

    tileIds.forEach((id) => {
      // Define tile sizes based on type
      let width = 2;
      let height = 2;
      
      if (id === 'calendar-summary') {
        width = 3;
        height = 3;
      } else if (id === 'daily-metrics') {
        width = 2;
        height = 3;
      }
      
      tiles.push({
        id,
        x,
        y,
        width,
        height
      });
      
      // Move position accounting for width
      x += width;
      if (x >= DEFAULT_GRID_COLS) {
        x = 0;
        y += height; // Move down by tile height
      }
    });

    return {
      tiles,
      gridCols: DEFAULT_GRID_COLS
    };
  }

  /**
   * Find nearest grid position for drop location
   * CRITICAL: This is the exact algorithm from the working codebase
   */
  static findNearestGridPosition(
    clientX: number, 
    clientY: number, 
    containerRect: DOMRect, 
    gridCols: number, 
    tileWidth: number = 2
  ): { x: number; y: number } {
    const containerWidth = containerRect.width;
    
    // Calculate cell dimensions accounting for gaps
    const totalGapWidth = GRID_GAP * (gridCols - 1);
    const cellWidth = (containerWidth - totalGapWidth) / gridCols;
    const cellHeight = GRID_CELL_HEIGHT + GRID_GAP; // Grid cell height + gap
    
    const relativeX = clientX - containerRect.left;
    const relativeY = clientY - containerRect.top;
    
    // Calculate grid position
    const rawGridX = Math.floor(relativeX / (cellWidth + GRID_GAP));
    const rawGridY = Math.floor(relativeY / cellHeight);
    
    // Ensure tile doesn't exceed grid boundaries based on its width
    const maxX = gridCols - tileWidth;
    const gridX = Math.max(0, Math.min(maxX, rawGridX));
    const gridY = Math.max(0, rawGridY);
    
    return { x: gridX, y: gridY };
  }

  /**
   * Check if a position is occupied by another tile
   */
  static isPositionOccupied(
    layout: DashboardLayout, 
    x: number, 
    y: number, 
    width: number,
    height: number,
    excludeTileId?: string
  ): boolean {
    return layout.tiles.some(tile => {
      if (tile.id === excludeTileId) return false;
      
      // Check if rectangles overlap
      return !(
        x >= tile.x + tile.width ||
        x + width <= tile.x ||
        y >= tile.y + tile.height ||
        y + height <= tile.y
      );
    });
  }

  /**
   * Find first available position for a tile of given dimensions
   */
  static findAvailablePosition(
    layout: DashboardLayout, 
    width: number, 
    height: number, 
    excludeTileId?: string
  ): { x: number; y: number } {
    for (let y = 0; y < 20; y++) { // Check up to 20 rows
      for (let x = 0; x <= layout.gridCols - width; x++) {
        if (!this.isPositionOccupied(layout, x, y, width, height, excludeTileId)) {
          return { x, y };
        }
      }
    }
    
    // If no position found, return bottom of grid
    const maxY = Math.max(0, ...layout.tiles.map(t => t.y + t.height));
    return { x: 0, y: maxY };
  }

  /**
   * Check if a drop position is valid (no collisions)
   */
  static isValidDropPosition(
    layout: DashboardLayout,
    tileId: string,
    x: number,
    y: number,
    width: number,
    height: number
  ): boolean {
    return !this.isPositionOccupied(layout, x, y, width, height, tileId);
  }

  /**
   * Attempt to place tile at position, return updated layout if valid, null if invalid
   */
  static tryPlaceTile(
    layout: DashboardLayout, 
    movedTileId: string, 
    newX: number, 
    newY: number
  ): DashboardLayout | null {
    const movedTile = layout.tiles.find(t => t.id === movedTileId);
    if (!movedTile) return null;
    
    // Check if the new position is valid (no collisions)
    const isValid = this.isValidDropPosition(
      layout, 
      movedTileId, 
      newX, 
      newY, 
      movedTile.width, 
      movedTile.height
    );
    
    if (!isValid) {
      return null; // Invalid drop - tile will snap back to original position
    }
    
    // Valid drop - update the tile position
    const newLayout = { ...layout };
    newLayout.tiles = layout.tiles.map(tile => {
      if (tile.id === movedTileId) {
        return { ...tile, x: newX, y: newY };
      }
      return tile;
    });
    
    return newLayout;
  }

  /**
   * Get CSS grid style properties for a tile position
   */
  static getTileGridStyle(position: TilePosition): React.CSSProperties {
    return {
      gridColumn: `${position.x + 1} / span ${position.width}`,
      gridRow: `${position.y + 1} / span ${position.height}`
    };
  }

  /**
   * Get grid template rows for the container
   */
  static getGridTemplateRows(layout: DashboardLayout): string {
    const maxY = Math.max(6, ...layout.tiles.map(t => t.y + t.height)); // Minimum 6 rows
    return `repeat(${maxY}, ${GRID_CELL_HEIGHT}px)`;
  }
}